/**
 * An agent's runs: newest first, one row per wake-up.
 *
 * Each row shows when it started, what triggered it (linked to the DM, card
 * or wiki page when the source carries one), a status pill, then
 * `9s · 14.3K in · 621 out · ≈ $0.004 · 2 tool calls` (how long it ran,
 * tokens, estimated cost, tool-call count). Expanding a row loads its steps,
 * every model call with its own cost, so the
 * listed costs add up to the run's. The list refreshes live from `agent.turn.*` frames (see
 * `useGlobalRealtime`), so a turn in progress shows as Running and settles
 * without a reload.
 *
 * Used by the Runs tab of Agent settings and by the runs dialog a
 * budget-exhausted / run-failed notification opens, so it takes only an
 * agent id and an optional "I navigated away" callback.
 */

import { useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useCallback, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import type {
  AgentRunDetail,
  AgentRunModelCall,
  AgentRunStatus,
  AgentRunSummary,
  AgentRunToolCall,
} from '@/api/types-bridge'
import { Button } from '@/components/Button'
import { EstimatedCost } from '@/components/EstimatedCost'
import { formatCompactNumber, formatRelativeTime } from '@/i18n/formatters'
import { formatDurationMs } from '@/lib/format-cost'
import { modelLabel } from '@/lib/model-label'

import { useTenantUsers } from '../use-tenant-users'
import { describeStoredErrorCode } from './agent-errors'
import { type RunTriggerLink, describeRunTrigger, resolveRunTriggerLink } from './run-trigger'
import { useAgentRun, useAgentRuns } from './use-agent-runs'

const STATUS_CLASS: Readonly<Record<AgentRunStatus, string>> = {
  running: 'bg-bg-muted text-fg',
  succeeded: 'bg-success/10 text-success-ink',
  failed: 'bg-danger/10 text-danger-ink',
  budget_exhausted: 'bg-warning/10 text-warning-ink',
  stopped: 'bg-bg-muted text-fg-muted',
}

/** The agent tools the BE registers; each has a `runs.tools.<name>` label. */
const KNOWN_TOOLS: ReadonlySet<string> = new Set([
  'search_cards',
  'get_card',
  'list_board',
  'create_card',
  'update_card',
  'move_card',
  'comment_on_card',
  'list_card_comments',
  'search_wiki',
  'read_wiki_doc',
  'create_wiki_draft',
  'update_wiki_draft',
  'list_wiki_comments',
  'reply_to_wiki_comment',
  'read_conversation',
  'send_message',
  'send_dm',
  'list_people',
  'react',
  'schedule_followup',
])

const STEP_STATUS_CLASS: Readonly<Record<AgentRunToolCall['status'], string>> = {
  pending: 'bg-bg-muted text-fg',
  succeeded: 'bg-success/10 text-success-ink',
  failed: 'bg-danger/10 text-danger-ink',
}

interface AgentRunsPanelProps {
  agentId: string
  /** Called just before a source link navigates, so a hosting dialog can close. */
  onNavigate?: () => void
}

/** Where a run's source link goes — the same destinations the notification bell uses. */
function useRunNavigation(onNavigate: (() => void) | undefined) {
  const navigate = useNavigate()
  return useCallback(
    (link: RunTriggerLink) => {
      onNavigate?.()
      if (link.type === 'conversation') {
        void navigate(
          link.messageId
            ? {
                to: '/messages/$conversationId',
                params: { conversationId: link.conversationId },
                search: { focusMessage: link.messageId },
              }
            : { to: '/messages/$conversationId', params: { conversationId: link.conversationId } },
        )
        return
      }
      if (link.type === 'card') {
        const search = {
          selectedDeliverable: link.cardId,
          commentId: link.commentId ?? undefined,
        }
        void navigate(
          link.projectId
            ? { to: '/board/$projectId', params: { projectId: link.projectId }, search }
            : { to: '/', search },
        )
        return
      }
      void navigate({
        to: '/wiki/d/$docId',
        params: { docId: link.docId },
        hash: link.threadId ? `thread-${link.threadId}` : undefined,
      }).then(() => {
        // The doc page is already mounted on a repeat visit and the router's
        // own history update emits no native `hashchange`.
        if (link.threadId) window.dispatchEvent(new HashChangeEvent('hashchange'))
      })
    },
    [navigate, onNavigate],
  )
}

export function AgentRunsPanel({ agentId, onNavigate }: AgentRunsPanelProps) {
  const { t } = useTranslation('agents')
  const runs = useAgentRuns(agentId)
  const users = useTenantUsers()
  const openLink = useRunNavigation(onNavigate)

  const nameById = useMemo(
    () => new Map((users.data ?? []).map((u) => [u.id, u.name || u.email])),
    [users.data],
  )
  const items = useMemo(() => (runs.data?.pages ?? []).flatMap((page) => page.data), [runs.data])

  if (runs.isLoading) {
    return <p className="text-sm text-fg-muted">{t('runs.loading')}</p>
  }
  if (runs.isError) {
    // 404 = the agent doesn't exist or the viewer can't read its runs;
    // retrying can't change that.
    if (runs.error instanceof ApiError && runs.error.status === 404) {
      return <p className="text-sm text-fg-muted">{t('runs.unavailable')}</p>
    }
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-sm text-danger-ink">
          {t('runs.loadFailed')}
        </p>
        <Button variant="secondary" onClick={() => void runs.refetch()}>
          {t('runs.retry')}
        </Button>
      </div>
    )
  }
  if (items.length === 0) {
    return <p className="text-sm text-fg-muted">{t('runs.empty')}</p>
  }

  return (
    <div className="flex flex-col gap-3">
      <ul aria-label={t('runs.listAria')} className="flex flex-col divide-y divide-border">
        {items.map((run) => (
          <RunRow
            key={run.id}
            agentId={agentId}
            run={run}
            actorName={
              run.trigger.actorUserId ? (nameById.get(run.trigger.actorUserId) ?? null) : null
            }
            onOpenLink={openLink}
          />
        ))}
      </ul>
      {runs.hasNextPage && (
        <div>
          <Button
            variant="secondary"
            onClick={() => void runs.fetchNextPage()}
            disabled={runs.isFetchingNextPage}
          >
            {runs.isFetchingNextPage ? t('runs.loadingMore') : t('runs.loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}

interface RunRowProps {
  agentId: string
  run: AgentRunSummary
  actorName: string | null
  onOpenLink: (link: RunTriggerLink) => void
}

function RunRow({ agentId, run, actorName, onOpenLink }: RunRowProps) {
  const { t, i18n } = useTranslation('agents')
  const [open, setOpen] = useState(false)
  const stepsId = useId()
  const locale = i18n.language || 'en'

  const label = describeRunTrigger(run.trigger, actorName)
  const link = resolveRunTriggerLink(run.trigger)
  const labelText = t(`runs.trigger.${label.key}`, { ...label.values })
  const startedLabel = formatRelativeTime(run.startedAt, locale)
  const running = run.status === 'running'
  // A running run has no duration yet. Prefer the BE's figure; fall back to
  // the timestamps for a payload that predates it.
  const durationMs = running
    ? null
    : (run.durationMs ??
      (run.finishedAt
        ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
        : null))
  const duration = durationMs === null ? null : formatDurationMs(durationMs, locale)

  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {link ? (
              <button
                type="button"
                onClick={() => onOpenLink(link)}
                title={t('runs.openSource')}
                className="truncate text-start text-sm font-medium text-fg underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
              >
                {labelText}
              </button>
            ) : (
              <span className="truncate text-sm font-medium text-fg">{labelText}</span>
            )}
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[run.status] ?? STATUS_CLASS.stopped}`}
            >
              {t(`runs.status.${run.status}`, { defaultValue: run.status })}
            </span>
            {run.attempt > 1 && (
              <span className="text-xs text-fg-muted">
                {t('runs.attempt', { attempt: run.attempt })}
              </span>
            )}
          </div>
          {label.note && <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{label.note}</p>}
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
            <time dateTime={run.startedAt}>{startedLabel}</time>
            <span aria-hidden="true">·</span>
            <span>{duration ?? (running ? t('runs.running') : '')}</span>
            <span aria-hidden="true">·</span>
            <span>
              {t('runs.tokens', {
                tokensIn: formatCompactNumber(run.tokensIn, locale),
                tokensOut: formatCompactNumber(run.tokensOut, locale),
              })}
            </span>
            {run.costUsd !== undefined && (
              <>
                <span aria-hidden="true">·</span>
                <EstimatedCost usd={run.costUsd} />
              </>
            )}
            <span aria-hidden="true">·</span>
            <span>{t('runs.toolCalls', { count: run.toolCallCount })}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={stepsId}
          aria-label={
            open
              ? t('runs.hideStepsFor', { trigger: labelText, time: startedLabel })
              : t('runs.showStepsFor', { trigger: labelText, time: startedLabel })
          }
          title={open ? t('runs.hideSteps') : t('runs.showSteps')}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-subtle"
        >
          <ChevronRight
            aria-hidden="true"
            className={`h-4 w-4 transition-transform rtl:-scale-x-100 ${open ? 'rotate-90 rtl:-rotate-90' : ''}`}
          />
        </button>
      </div>
      <div id={stepsId} hidden={!open}>
        {open && <RunSteps agentId={agentId} run={run} />}
      </div>
    </li>
  )
}

/** One step of a run: the model calls made for it, then the tool calls they issued. */
interface RunStepGroup {
  key: string
  modelCalls: { call: AgentRunModelCall; retry: boolean }[]
  toolCalls: AgentRunToolCall[]
}

/**
 * Group a run's calls by step. Every model call is kept, in attempt order, so
 * the listed costs add up to the run's cost; a step that was attempted more
 * than once marks its later model calls as retries. Model calls with no step
 * go last on their own.
 */
const groupRunSteps = (
  modelCalls: readonly AgentRunModelCall[],
  toolCalls: readonly AgentRunToolCall[],
): RunStepGroup[] => {
  const steps = new Map<number, RunStepGroup>()
  const groupFor = (step: number): RunStepGroup => {
    let group = steps.get(step)
    if (!group) {
      group = { key: `step-${step}`, modelCalls: [], toolCalls: [] }
      steps.set(step, group)
    }
    return group
  }
  for (const call of toolCalls) groupFor(call.step).toolCalls.push(call)

  const unstepped: RunStepGroup['modelCalls'] = []
  const ordered = modelCalls
    .map((call, index) => ({ call, index }))
    .sort((a, b) => (a.call.attempt ?? 0) - (b.call.attempt ?? 0) || a.index - b.index)
  for (const { call } of ordered) {
    if (call.step === null) {
      unstepped.push({ call, retry: false })
      continue
    }
    const group = groupFor(call.step)
    group.modelCalls.push({ call, retry: group.modelCalls.length > 0 })
  }

  const groups = [...steps.entries()].sort((a, b) => a[0] - b[0]).map(([, group]) => group)
  if (unstepped.length > 0) groups.push({ key: 'no-step', modelCalls: unstepped, toolCalls: [] })
  return groups
}

function RunSteps({ agentId, run }: { agentId: string; run: AgentRunSummary }) {
  const { t } = useTranslation('agents')
  const detail = useAgentRun(agentId, run.id)

  return (
    <div className="mt-2 rounded-md border border-border bg-bg-muted/40 px-3 py-2">
      {run.model && (
        <p className="text-xs text-fg-muted" title={run.model}>
          {t('runs.model', { model: modelLabel(run.model) })}
        </p>
      )}
      {run.errorCode && (
        <p
          className={`mt-0.5 text-xs ${
            run.errorCode === 'private_endpoint_requires_premium'
              ? 'text-fg-muted'
              : 'text-danger-ink'
          }`}
        >
          {describeStoredErrorCode(run.errorCode, t, t('runs.errorUnknown'))}
        </p>
      )}
      {detail.isLoading ? (
        <p className="mt-1 text-xs text-fg-muted">{t('runs.steps.loading')}</p>
      ) : detail.isError || !detail.data ? (
        <p role="alert" className="mt-1 text-xs text-danger-ink">
          {t('runs.steps.loadFailed')}
        </p>
      ) : detail.data.toolCalls.length === 0 && (detail.data.modelCalls ?? []).length === 0 ? (
        <p className="mt-1 text-xs text-fg-muted">{t('runs.steps.empty')}</p>
      ) : (
        <RunStepList detail={detail.data} />
      )}
    </div>
  )
}

function RunStepList({ detail }: { detail: AgentRunDetail }) {
  const { t } = useTranslation('agents')
  const groups = groupRunSteps(detail.modelCalls ?? [], detail.toolCalls)
  return (
    <ol aria-label={t('runs.steps.listAria')} className="mt-1 flex flex-col gap-1.5">
      {groups.flatMap((group) => [
        ...group.modelCalls.map(({ call, retry }, index) => (
          <li key={`${group.key}-model-${call.attempt ?? 'x'}-${index}`} className="text-xs">
            <div className="flex flex-wrap items-center gap-2 text-fg-muted">
              <span>{retry ? t('runs.steps.modelCallRetry') : t('runs.steps.modelCall')}</span>
              <span aria-hidden="true">·</span>
              <EstimatedCost usd={call.costUsd} />
            </div>
          </li>
        )),
        ...group.toolCalls.map((call) => (
          <li key={`${group.key}-tool-${call.callIndex}`} className="text-xs">
            <div className="flex flex-wrap items-center gap-2">
              {KNOWN_TOOLS.has(call.tool) ? (
                <span className="font-medium text-fg">{t(`runs.tools.${call.tool}` as never)}</span>
              ) : (
                <>
                  <span className="font-medium text-fg">{t('runs.tools.unknown')}</span>
                  <span className="font-mono text-fg-muted">{call.tool}</span>
                </>
              )}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${STEP_STATUS_CLASS[call.status]}`}
              >
                {t(`runs.steps.status.${call.status}`)}
              </span>
            </div>
            {call.status === 'failed' && (
              <p className="mt-0.5 text-danger-ink">
                {describeStoredErrorCode(call.errorCode, t, t('runs.errorUnknown'))}
              </p>
            )}
          </li>
        )),
      ])}
    </ol>
  )
}
